import { Seite } from '../app.jsx'
import { api } from '../lib/api.js'
import { useLaden } from '../lib/laden.js'
import { datumText, zahl } from '../lib/format.js'
import { Laden, Fehler } from '../components/Zustand.jsx'

/**
 * Statistiken. Erst durch die reviews-Tabelle sinnvoll möglich; die 47 Tage
 * Vorgeschichte aus der YouTube-App liegen nur als Tagessummen vor und kommen
 * über dieselbe Heatmap mit herein (Grenze: meta.migrated_on).
 *
 * Die Heatmap-Rampe ist eine der vier Stellen, an denen DESIGN.md den Akzent
 * zulässt — fünf Stufen von --akzent-gedaempft bis --akzent.
 */

const RATING_LABEL = { again: 'Wieder', hard: 'Schwer', good: 'Gut', easy: 'Einfach' }

/** Fünf Stufen: 0 = nichts gelernt, 4 = stärkster Tag. */
function stufeFuer(anzahl, hoechst) {
  if (!anzahl) return 0
  if (hoechst <= 1) return 4
  return Math.max(1, Math.min(4, Math.ceil((anzahl / hoechst) * 4)))
}

function Block({ titel, children, weit }) {
  return (
    <section class={`block ${weit ? 'block--weit' : ''}`}>
      <h2 class="block__titel">{titel}</h2>
      {children}
    </section>
  )
}

function Heatmap({ tage }) {
  const hoechst = tage.reduce((m, t) => Math.max(m, t.count), 0)
  return (
    <div class="heatmap" role="img" aria-label="Lerntage der letzten 52 Wochen">
      {tage.map((t) => (
        <span
          key={t.day}
          class="heatmap__tag"
          data-stufe={stufeFuer(t.count, hoechst)}
          title={`${datumText(t.day)}: ${t.count} ${t.count === 1 ? 'Bewertung' : 'Bewertungen'}`}
        />
      ))}
    </div>
  )
}

function Vorschau({ tage }) {
  const hoechst = tage.reduce((m, t) => Math.max(m, t.count), 0) || 1
  return (
    <div class="vorschau">
      {tage.map((t) => (
        <div
          key={t.day}
          class="vorschau__balken"
          style={{ '--hoehe': `${Math.round((t.count / hoechst) * 100)}%` }}
          title={`${datumText(t.day)}: ${t.count} fällig`}
        />
      ))}
    </div>
  )
}

function Verteilung({ werte }) {
  const summe = Object.values(werte).reduce((a, b) => a + b, 0) || 1
  return (
    <ul class="verteilung">
      {Object.entries(RATING_LABEL).map(([schluessel, label]) => (
        <li key={schluessel} class="verteilung__zeile">
          <span class="verteilung__label">{label}</span>
          <span class="verteilung__balken">
            <span
              class="verteilung__fuellung"
              style={{ width: `${Math.round((werte[schluessel] / summe) * 100)}%` }}
            />
          </span>
          <span class="verteilung__wert zahl">{zahl(werte[schluessel])}</span>
        </li>
      ))}
    </ul>
  )
}

export function Statistiken() {
  const { daten, laedt, fehler, neuLaden } = useLaden(() => api.get('/api/stats'), [])

  if (laedt) {
    return (
      <Seite titel="Statistiken">
        <Laden />
      </Seite>
    )
  }
  if (fehler) {
    return (
      <Seite titel="Statistiken">
        <Fehler fehler={fehler} erneut={neuLaden} />
      </Seite>
    )
  }

  const { heatmap, streak, forecast, verteilung, reifegrad, gesamt } = daten
  const nieGelernt =
    streak.laengste === 0 && Object.values(verteilung.insgesamt).every((v) => !v)

  return (
    <Seite titel="Statistiken">
      <div class="statistiken">
        <Block titel="Serie">
          <p class="kennzahl">{streak.aktuell}</p>
          <p class="meta">
            {streak.aktuell === 1 ? 'Tag in Folge' : 'Tage in Folge'} · längste Serie{' '}
            <span class="zahl">{streak.laengste}</span>
          </p>
        </Block>

        <Block titel="Karten">
          <p class="kennzahl">{zahl(gesamt.karten)}</p>
          <p class="meta">
            <span class="zahl">{zahl(gesamt.faellig_heute)}</span> heute fällig ·{' '}
            <span class="zahl">{zahl(gesamt.mit_lernstand)}</span> mit Lernstand
            {gesamt.archiviert ? (
              <>
                {' · '}
                <span class="zahl">{zahl(gesamt.archiviert)}</span> archiviert
              </>
            ) : null}
          </p>
        </Block>

        <Block titel="Reifegrad">
          <ul class="reifegrad">
            {[
              ['neu', reifegrad.neu],
              ['im Lernen', reifegrad.im_lernen],
              ['reif', reifegrad.reif],
            ].map(([label, wert]) => (
              <li key={label} class="reifegrad__zeile">
                <span class="reifegrad__label">{label}</span>
                <span class="reifegrad__wert zahl">{zahl(wert)}</span>
              </li>
            ))}
          </ul>
          <p class="meta">Reif ab 21 Tagen Intervall.</p>
        </Block>

        <Block titel="Lerntage" weit>
          {nieGelernt ? (
            <p class="meta">Noch keine Bewertungen aufgezeichnet.</p>
          ) : (
            <>
              <Heatmap tage={heatmap} />
              <p class="meta">
                Letzte 52 Wochen. Tage bis zum Umzug am {datumText(daten.migrated_on)} stammen aus
                der Vorgeschichte, danach aus den Einzel-Bewertungen.
              </p>
            </>
          )}
        </Block>

        <Block titel="Fällig in den nächsten 30 Tagen" weit>
          <Vorschau tage={forecast} />
          <p class="meta">
            Höchster Tag:{' '}
            <span class="zahl">{zahl(forecast.reduce((m, t) => Math.max(m, t.count), 0))}</span>{' '}
            Karten
          </p>
        </Block>

        <Block titel="Bewertungen seit dem Umzug">
          <Verteilung werte={verteilung.seit_umzug} />
        </Block>

        <Block titel="Bewertungen insgesamt">
          <Verteilung werte={verteilung.insgesamt} />
          <p class="meta">Einschließlich der Bewertungen aus der YouTube-App.</p>
        </Block>
      </div>
    </Seite>
  )
}
